import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns/format';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import { useMemo, useState, useEffect, useRef } from 'react';
import { type DateRange } from 'react-day-picker';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/date-picker-with-range';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { TextInput } from '@/components/ui/form/input';
import { IconButton } from '@/components/ui/IconButton';
import { YStack } from '@/components/ui/layout/layout';
import { Loader } from '@/components/ui/Loader';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';

import {
  inputsDbAtoms,
  type InputsDbItem,
  type InputsDbSort,
  type Metadata,
  getCkJobId,
  getItemMetadata,
  getBM25Score,
  getVectorScore,
} from './inputs-db.atoms';
import {
  useInputsDbData,
  useUpdateFilters,
  useResetFilters,
} from './inputs-db.query';
import { TextViewerDialog } from './text-viewer-dialog';

export const InputsDb = observer(() => {
  const [selectedText, setSelectedText] = useState<string>('');
  const [isTextViewerOpen, setIsTextViewerOpen] = useState(false);
  const [dialogInitialSearch, setDialogInitialSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<
    Metadata | undefined
  >(undefined);
  const [selectedInputId, setSelectedInputId] = useState<string>('');
  const [selectedItemType, setSelectedItemType] = useState<string>('');
  const [selectedTextSize, setSelectedTextSize] = useState<number>(0);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const hasInitialized = useRef(false);

  const filters = inputsDbAtoms.useFilters();

  const { data: queryData, isLoading: loading, refetch } = useInputsDbData();
  const updateFilters = useUpdateFilters();
  const resetFilters = useResetFilters();

  const debouncedUpdateSearch = useRef(
    debounce((value: string) => {
      updateFilters({ search: value });
    }, 300)
  ).current;

  useEffect(() => {
    return () => debouncedUpdateSearch.cancel();
  }, [debouncedUpdateSearch]);

  const searchMetadata = queryData?.search_metadata;

  const data = useMemo(() => {
    let rawData = queryData?.results ? [...queryData.results] : [];

    const seenIds = new Set<string>();
    rawData = rawData.filter((item) => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });

    return rawData;
  }, [queryData?.results]);
  const availableApps = useMemo(() => {
    const serverApps = queryData?.apps || [];
    const recordApps = [...new Set(data.map((item) => item.app))];
    const allApps = [...new Set([...serverApps, ...recordApps])];
    return allApps.sort();
  }, [queryData, data]);

  const dateRange = useMemo(() => {
    const range: Partial<DateRange> = {};
    if (filters.after) {
      range.from = new Date(filters.after);
    }
    if (filters.before) {
      range.to = new Date(filters.before);
    }
    return Object.keys(range).length > 0 ? (range as DateRange) : undefined;
  }, [filters.after, filters.before]);

  useEffect(() => {
    if (!hasInitialized.current && filters.type.length === 0) {
      hasInitialized.current = true;
      updateFilters({ type: ['input'] });
    }
  }, [filters.type.length, updateFilters]);

  useEffect(() => {
    setSearchInputValue(filters.search);
  }, [filters.search]);

  const typeOptions = [
    'input',
    'summary',
    'message',
    'daily_summary',
    'lb_chat',
  ];

  const columns = useMemo<ColumnDef<InputsDbItem, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        accessorKey: 'id',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-foreground">
            {row.original.id}
          </span>
        ),
        size: 120,
      },
      {
        id: 'app',
        header: 'App',
        accessorKey: 'app',
        cell: ({ row }) => (
          <span className="text-sm text-foreground">{row.original.app}</span>
        ),
        size: 100,
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        cell: ({ row }) => (
          <span className="text-sm text-foreground">{row.original.type}</span>
        ),
        size: 80,
      },
      {
        id: 'timestamp',
        header: 'Timestamp',
        accessorKey: 'created_at',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {format(new Date(row.original.created_at), 'yyyy-MM-dd HH:mm:ss')}
          </span>
        ),
        size: 150,
      },
      {
        id: 'ck_job_id',
        header: 'CK Job ID',
        accessorFn: (row) => getCkJobId(row.metadata),
        cell: ({ row }) => {
          const ckJobId = getCkJobId(row.original.metadata);

          if (!ckJobId) {
            return <span className="text-xs text-foreground-muted">-</span>;
          }

          return (
            <button
              className="font-mono text-xs text-primary hover:underline text-left truncate block max-w-[180px]"
              onClick={() => inputsDbAtoms.showInputsForCkJobId(ckJobId)}
              title={ckJobId}
            >
              {ckJobId}
            </button>
          );
        },
        size: 180,
      },
      {
        id: 'text',
        header: ({ column }) => {
          const sortDirection = column.getIsSorted();
          return (
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => {
                const currentSort = column.getIsSorted();
                if (currentSort === false) {
                  column.toggleSorting(false);
                } else if (currentSort === 'asc') {
                  column.toggleSorting(true);
                } else {
                  column.clearSorting();
                }
              }}
            >
              Text
              {sortDirection === 'asc' ? (
                <Icons.arrowUp className="h-4 w-4" />
              ) : sortDirection === 'desc' ? (
                <Icons.arrowDown className="h-4 w-4" />
              ) : null}
            </Button>
          );
        },
        accessorKey: 'text',
        sortingFn: (rowA, rowB) => {
          const sizeA = rowA.original.text_size;
          const sizeB = rowB.original.text_size;
          return sizeA - sizeB;
        },
        cell: ({ row }) => {
          const text = row.original.text;
          const truncatedText =
            text.length > 200 ? `${text.substring(0, 200)}...` : text;

          return (
            <button
              className="text-sm text-foreground text-left hover:text-primary cursor-pointer underline-offset-2 hover:underline max-w-[300px] block break-all"
              onClick={() => {
                setSelectedText(text);
                setDialogInitialSearch(filters.search);
                setSelectedMetadata(row.original.metadata);
                setSelectedInputId(row.original.id);
                setSelectedItemType(row.original.type);
                setSelectedTextSize(row.original.text_size);
                setIsTextViewerOpen(true);
              }}
            >
              {truncatedText}
            </button>
          );
        },
        size: 300,
        enableSorting: true,
      },
      {
        id: 'characters',
        header: 'Characters',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {row.original.text_size}
          </span>
        ),
        size: 80,
      },
      {
        id: 'bm25_score',
        accessorFn: (row) => {
          const metadata = getItemMetadata(row.id, searchMetadata);
          return getBM25Score(metadata);
        },
        header: ({ column }) => {
          const sortDirection = column.getIsSorted();
          return (
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => {
                const currentSort = column.getIsSorted();
                if (currentSort === false) {
                  column.toggleSorting(false);
                } else if (currentSort === 'asc') {
                  column.toggleSorting(true);
                } else {
                  column.clearSorting();
                }
              }}
            >
              BM25
              {sortDirection === 'asc' ? (
                <Icons.arrowUp className="h-4 w-4" />
              ) : sortDirection === 'desc' ? (
                <Icons.arrowDown className="h-4 w-4" />
              ) : null}
            </Button>
          );
        },
        sortingFn: (rowA, rowB) => {
          const scoreA = rowA.getValue('bm25_score') as number | null;
          const scoreB = rowB.getValue('bm25_score') as number | null;

          if (scoreA === null && scoreB === null) return 0;
          if (scoreA === null) return 1;
          if (scoreB === null) return -1;

          return scoreA - scoreB;
        },
        cell: ({ row }) => {
          const metadata = getItemMetadata(row.original.id, searchMetadata);
          const score = getBM25Score(metadata);
          return score !== null ? (
            <span className="text-xs text-foreground-muted font-mono">
              {score.toFixed(4)}
            </span>
          ) : (
            <span className="text-xs text-foreground-muted">-</span>
          );
        },
        size: 80,
        enableSorting: true,
      },
      {
        id: 'vector_score',
        accessorFn: (row) => {
          const metadata = getItemMetadata(row.id, searchMetadata);
          return getVectorScore(metadata);
        },
        header: ({ column }) => {
          const sortDirection = column.getIsSorted();
          return (
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => {
                const currentSort = column.getIsSorted();
                if (currentSort === false) {
                  column.toggleSorting(false);
                } else if (currentSort === 'asc') {
                  column.toggleSorting(true);
                } else {
                  column.clearSorting();
                }
              }}
            >
              Vector
              {sortDirection === 'asc' ? (
                <Icons.arrowUp className="h-4 w-4" />
              ) : sortDirection === 'desc' ? (
                <Icons.arrowDown className="h-4 w-4" />
              ) : null}
            </Button>
          );
        },
        sortingFn: (rowA, rowB) => {
          const scoreA = rowA.getValue('vector_score') as number | null;
          const scoreB = rowB.getValue('vector_score') as number | null;

          if (scoreA === null && scoreB === null) return 0;
          if (scoreA === null) return 1;
          if (scoreB === null) return -1;

          return scoreA - scoreB;
        },
        cell: ({ row }) => {
          const metadata = getItemMetadata(row.original.id, searchMetadata);
          const score = getVectorScore(metadata);
          return score !== null ? (
            <span className="text-xs text-foreground-muted font-mono">
              {score.toFixed(4)}
            </span>
          ) : (
            <span className="text-xs text-foreground-muted">-</span>
          );
        },
        size: 80,
        enableSorting: true,
      },
    ],
    [filters.search, searchMetadata]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: true,
    getRowId: (row) => row.id,
  });

  const handleTypeFilter = (type: string) => {
    const currentTypes = filters.type;
    let newTypes: string[];

    if (currentTypes.includes(type)) {
      newTypes = currentTypes.filter((t) => t !== type);
    } else {
      newTypes = [...currentTypes, type];
    }

    updateFilters({ type: newTypes });
  };

  const handleAppFilter = (app: string) => {
    updateFilters({ app });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    debouncedUpdateSearch(value);
  };

  const handleSearchSubmit = () => {
    debouncedUpdateSearch.cancel();
    updateFilters({ search: searchInputValue });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleHybridSearchToggle = (checked: boolean) => {
    updateFilters({ isHybridSearch: checked });
  };

  const handleDateRangeChange = (dateRange: DateRange | undefined) => {
    let after: string = '';
    let before: string = '';

    if (dateRange?.from) {
      const startDate = new Date(dateRange.from);
      startDate.setHours(0, 0, 0, 0);
      after = startDate.toISOString();

      const endDate = new Date(dateRange.to || dateRange.from);
      endDate.setHours(23, 59, 59, 999);
      before = endDate.toISOString();
    }

    updateFilters({ after, before });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const getTypeDisplayText = () => {
    if (filters.type.length === 0) return 'Select Types';
    return filters.type.join(', ');
  };

  const handleResetFilters = () => {
    debouncedUpdateSearch.cancel();
    resetFilters();
    setSearchInputValue('');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-4 p-4 border-b border-border">
        <div className="flex items-center gap-4 flex-wrap">
          <DropdownMenuRoot>
            <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-surface hover:bg-secondary-hover">
              <span className="text-sm">{getTypeDisplayText()}</span>
              <Icons.chevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Select Types (Multi-select)</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {typeOptions.map((type) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => handleTypeFilter(type)}
                  className={
                    filters.type.includes(type) ? 'bg-secondary-active' : ''
                  }
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3 h-3 border border-border rounded-sm ${filters.type.includes(type) ? 'bg-accent' : ''}`}
                    >
                      {filters.type.includes(type) && (
                        <div className="w-full h-full flex items-center justify-center text-accent-foreground text-xs">
                          ✓
                        </div>
                      )}
                    </div>
                    {type}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenuRoot>

          <DropdownMenuRoot>
            <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-surface hover:bg-secondary-hover">
              <span className="text-sm">
                {filters.app ? filters.app : 'All Apps'}
              </span>
              <Icons.chevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Filter by App</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => updateFilters({ app: '' })}
                className={!filters.app ? 'bg-secondary-active' : ''}
              >
                All Apps
              </DropdownMenuItem>
              {availableApps.length === 0 ? (
                <DropdownMenuItem disabled>No apps available</DropdownMenuItem>
              ) : (
                availableApps.map((app) => (
                  <DropdownMenuItem
                    key={app}
                    onClick={() => handleAppFilter(app)}
                    className={filters.app === app ? 'bg-secondary-active' : ''}
                  >
                    {app}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenuRoot>

          <DatePicker.Root
            date={dateRange}
            onDateChange={handleDateRangeChange}
            placeholder="Filter by date range"
            disabled={loading}
          >
            <DatePicker.Trigger>
              <Button
                variant="outline"
                disabled={loading}
                className="w-[280px] justify-start text-left font-normal"
              >
                <Icons.search className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, 'LLL dd, y')} -{' '}
                      {format(dateRange.to, 'LLL dd, y')}
                    </>
                  ) : (
                    format(dateRange.from, 'LLL dd, y')
                  )
                ) : (
                  <span>Filter by date range</span>
                )}
              </Button>
            </DatePicker.Trigger>
            <DatePicker.Content />
          </DatePicker.Root>

          <button
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-surface hover:bg-secondary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
          >
            <Icons.refresh
              className={`h-4 w-4 ${loading || isRefreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm">
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </span>
          </button>

          {(filters.type.length > 0 ||
            filters.app ||
            filters.search ||
            filters.ckJobId ||
            filters.after ||
            filters.before ||
            filters.isHybridSearch) && (
            <button
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-surface hover:bg-secondary-hover text-destructive"
              onClick={handleResetFilters}
            >
              <span className="text-sm">Reset Filters</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Icons.search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <TextInput
              placeholder="Search text..."
              value={searchInputValue}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="pl-10 w-full"
            />
          </div>

          <div className="flex items-center gap-3 px-4 py-2 border border-border rounded-md bg-surface whitespace-nowrap">
            <Switch
              checked={filters.isHybridSearch || false}
              onCheckedChange={handleHybridSearchToggle}
              disabled={loading}
            />
            <span className="text-sm text-foreground">Hybrid Search</span>
          </div>

          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <IconButton
                size="lg"
                variant="outline"
                icon={Icons.sort}
                tooltip={
                  filters.sortBy === 'recent'
                    ? 'Sort: Newest first'
                    : 'Sort: Relevance'
                }
                disabled={loading}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-0 w-44">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={filters.sortBy}
                onValueChange={(value) =>
                  updateFilters({ sortBy: value as InputsDbSort })
                }
              >
                <DropdownMenuRadioItem value="relevance">
                  Relevance
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="recent">
                  Newest first
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenuRoot>
        </div>

        {filters.ckJobId && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-muted">CK Job ID:</span>
            <span
              className="font-mono text-xs text-foreground px-2 py-1 rounded bg-surface border border-border"
              title={filters.ckJobId}
            >
              {filters.ckJobId}
            </span>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => updateFilters({ ckJobId: '' })}
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      <YStack className="flex-1 gap-4 justify-between overflow-hidden p-4">
        <div className="max-h-full overflow-y-hidden">
          <ScrollArea className="max-h-full border rounded-md border-border">
            <Table>
              <TableHeader className="border-b border-border bg-surface">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="border-none">
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className="py-4 text-foreground-subtle"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center p-12"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <div className="size-8">
                          <Loader />
                        </div>
                        <span className="text-foreground-muted">
                          Loading data...
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="text-center p-12"
                    >
                      <p className="text-foreground-subtle">
                        No data available yet!
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.original.id}
                      className="hover:bg-surface border-border"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </YStack>

      <TextViewerDialog
        text={selectedText}
        isOpen={isTextViewerOpen}
        onClose={() => setIsTextViewerOpen(false)}
        initialSearchText={dialogInitialSearch}
        metadata={selectedMetadata}
        inputId={selectedInputId}
        itemType={selectedItemType}
        textSize={selectedTextSize}
      />
    </div>
  );
});
